import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Mail, Pencil, Trash2, MoreVertical, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/frontend/components/ui/button';
import { Input } from '@/frontend/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/frontend/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/frontend/components/ui/card';
import { Badge } from '@/frontend/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/frontend/components/ui/dialog';
import { Label } from '@/frontend/components/ui/label';
import { PeriodSelector } from '@/frontend/components/energy/PeriodSelector';
import {
  getConsumptionByResidence,
  calculateSummary,
  formatBillingPeriodDisplay,
  deleteEnergyConsumption,
  updateEnergyConsumption,
  markAsPaid,
  markAsUnpaid,
} from '@/backend/services/energyConsumption';
import { useAdminResidences } from '@/backend/hooks/admin/useAdminResidences';
import { useSendConsumptionNotification } from '@/backend/hooks/admin/useEnergyNotifications';
import { useToast } from '@/backend/hooks/use-toast';
import type { EnergyConsumption } from '@/shared/types/energy';

type PeriodFilter = 'specific' | 'last3' | 'last6' | 'all';

export default function EnergyConsumption() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const sendNotification = useSendConsumptionNotification();
  const [selectedResidenceId, setSelectedResidenceId] = useState<string>('');
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('specific');
  const [selectedPeriod, setSelectedPeriod] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<EnergyConsumption | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState<EnergyConsumption | null>(null);
  const [editFormData, setEditFormData] = useState({ consumptionKwh: 0 });
  const [recentlyPaidId, setRecentlyPaidId] = useState<string | null>(null);

  // Fetch residences
  const { data: residences = [] } = useAdminResidences();

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (record: EnergyConsumption) => deleteEnergyConsumption(record),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['energy-consumption'] });
      queryClient.invalidateQueries({ queryKey: ['my-consumption'] });
      toast({
        title: 'Success',
        description: 'Consumption record deleted successfully',
      });
      setDeleteDialogOpen(false);
      setRecordToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete consumption record',
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateEnergyConsumption>[1] }) =>
      updateEnergyConsumption(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['energy-consumption'] });
      queryClient.invalidateQueries({ queryKey: ['my-consumption'] });
      toast({
        title: 'Success',
        description: 'Consumption record updated successfully',
      });
      setEditDialogOpen(false);
      setRecordToEdit(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update consumption record',
        variant: 'destructive',
      });
    },
  });

  // Mark paid/unpaid mutations
  const markPaidMutation = useMutation({
    mutationFn: (record: EnergyConsumption) => markAsPaid(record),
    onSuccess: (_, record) => {
      setRecentlyPaidId(record.id);
      setTimeout(() => setRecentlyPaidId(null), 1200);
      queryClient.invalidateQueries({ queryKey: ['energy-consumption'] });
      queryClient.invalidateQueries({ queryKey: ['my-consumption'] });
      toast({ title: 'Success', description: 'Record marked as paid' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const markUnpaidMutation = useMutation({
    mutationFn: (record: EnergyConsumption) => markAsUnpaid(record),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['energy-consumption'] });
      queryClient.invalidateQueries({ queryKey: ['my-consumption'] });
      toast({ title: 'Success', description: 'Record marked as unpaid' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Fetch consumption records
  const { data: consumptionRecords = [], isLoading } = useQuery({
    queryKey: ['energy-consumption', selectedResidenceId, periodFilter === 'specific' ? selectedPeriod : periodFilter],
    queryFn: async () => {
      if (!selectedResidenceId) return [];
      // For specific period, filter by month/year; otherwise get all and filter on frontend
      const records = await getConsumptionByResidence(
        selectedResidenceId,
        periodFilter === 'specific' ? {
          month: selectedPeriod.month,
          year: selectedPeriod.year,
        } : undefined
      );
      
      return records;
    },
    enabled: !!selectedResidenceId,
  });

  // Filter by period and search query
  const filteredRecords = useMemo(() => {
    let records = consumptionRecords;

    // Apply time period filter (only for non-specific filters)
    if (periodFilter !== 'specific' && records.length > 0) {
      const now = new Date();
      const cutoffDate = new Date();

      if (periodFilter === 'last3') {
        cutoffDate.setMonth(now.getMonth() - 3);
      } else if (periodFilter === 'last6') {
        cutoffDate.setMonth(now.getMonth() - 6);
      }
      // For 'all', don't filter by date

      if (periodFilter !== 'all') {
        records = records.filter(record => {
          const recordDate = new Date(record.billingPeriod.year, record.billingPeriod.month - 1);
          return recordDate >= cutoffDate;
        });
      }
    }

    // Apply search query filter (case insensitive with accent normalization)
    if (searchQuery.trim()) {
      const normalizeText = (text: string) =>
        text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      const queryNormalized = normalizeText(searchQuery.trim());
      records = records.filter(
        (record) =>
          normalizeText(record.roomTypeName).includes(queryNormalized) ||
          (record.studentName && normalizeText(record.studentName).includes(queryNormalized)) ||
          (record.studentEmail && normalizeText(record.studentEmail).includes(queryNormalized))
      );
    }

    return records;
  }, [consumptionRecords, periodFilter, searchQuery]);

  // Calculate summary
  const summary = useMemo(
    () => calculateSummary(filteredRecords),
    [filteredRecords]
  );

  const getStatusBadge = (record: typeof consumptionRecords[0]) => {
    if (!record.studentId) {
      return (
        <Badge variant="secondary" className="gap-1">
          ℹ️ No contract
        </Badge>
      );
    }

    if (record.exceedsLimit) {
      return (
        <Badge className="gap-1 bg-red-100 text-red-800 border-red-200">
          +{record.excessKwh} kWh
        </Badge>
      );
    }

    return (
      <Badge className="gap-1 bg-emerald-100 text-emerald-800 border-emerald-200">
        Within limit
      </Badge>
    );
  };

  const getPaymentBadge = (record: EnergyConsumption) => {
    if (!record.exceedsLimit || record.paymentStatus === null) return <span className="text-muted-foreground">—</span>;

    if (record.paymentStatus === 'paid') {
      const date = record.paidAt
        ? (record.paidAt as unknown as { toDate(): Date }).toDate().toLocaleDateString('en-GB')
        : '';
      const isNew = recentlyPaidId === record.id;
      return (
        <div className="space-y-0.5">
          <Badge className={`gap-1 bg-emerald-100 text-emerald-800 border-emerald-200${isNew ? ' animate-paid-success' : ''}`}>
            <CheckCircle className="h-3 w-3" />
            Paid
          </Badge>
          {date && <div className="text-xs text-muted-foreground">{date}</div>}
        </div>
      );
    }

    return (
      <Badge className="gap-1 bg-red-100 text-red-800 border-red-200">
        <XCircle className="h-3 w-3" />
        Unpaid
      </Badge>
    );
  };

  // Action handlers
  const handleEdit = (record: EnergyConsumption) => {
    setRecordToEdit(record);
    setEditFormData({ consumptionKwh: record.consumptionKwh });
    setEditDialogOpen(true);
  };

  const handleDelete = (record: EnergyConsumption) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (recordToDelete) {
      deleteMutation.mutate(recordToDelete);
    }
  };

  const handleSaveEdit = () => {
    if (!recordToEdit) return;

    // Validate
    if (!editFormData.consumptionKwh || editFormData.consumptionKwh <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Consumption must be a positive number',
        variant: 'destructive',
      });
      return;
    }

    const newKwh = editFormData.consumptionKwh;
    const limit = recordToEdit.contractMonthlyLimit;
    const exceedsLimit = limit != null ? newKwh > limit : false;
    const excessKwh = exceedsLimit && limit != null ? newKwh - limit : null;

    // Derive the per-kWh rate from the original record if available
    const originalRate =
      recordToEdit.excessKwh && recordToEdit.excessAmountEur != null
        ? recordToEdit.excessAmountEur / recordToEdit.excessKwh
        : null;
    const excessAmountEur =
      excessKwh != null && originalRate != null
        ? parseFloat((excessKwh * originalRate).toFixed(2))
        : null;

    // Reset payment status when consumption changes
    const paymentStatus = exceedsLimit ? 'unpaid' : null;

    updateMutation.mutate({
      id: recordToEdit.id,
      data: {
        consumptionKwh: newKwh,
        exceedsLimit,
        excessKwh,
        excessAmountEur,
        paymentStatus,
        paidAt: null,
      },
    });
  };

  const handleSendEmail = (record: EnergyConsumption) => {
    if (!record.studentEmail) {
      toast({
        title: 'Cannot send email',
        description: 'No email address found for this student',
        variant: 'destructive',
      });
      return;
    }
    sendNotification.mutate(record);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold">Energy Consumption</h1>
        <Button onClick={() => navigate('/admin/energy/new')} className="flex-shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Register Consumption
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={selectedResidenceId} onValueChange={setSelectedResidenceId}>
              <SelectTrigger className="w-full md:w-80">
                <SelectValue placeholder="Select residence" />
              </SelectTrigger>
              <SelectContent>
                {residences.map((residence) => (
                  <SelectItem key={residence.id} value={residence.id}>
                    {residence.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={periodFilter} onValueChange={(value: PeriodFilter) => setPeriodFilter(value)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="specific">Specific Month</SelectItem>
                <SelectItem value="last3">Last 3 Months</SelectItem>
                <SelectItem value="last6">Last 6 Months</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>

            {periodFilter === 'specific' && (
              <PeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
            )}

            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by room or student..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Consumption Records
            {periodFilter === 'specific' && ` - ${formatBillingPeriodDisplay(selectedPeriod.month, selectedPeriod.year)}`}
            {periodFilter === 'last3' && ' - Last 3 Months'}
            {periodFilter === 'last6' && ' - Last 6 Months'}
            {periodFilter === 'all' && ' - All Time'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {selectedResidenceId
                ? 'No records found for this period'
                : 'Select a residence to view consumption'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Room</th>
                      <th className="text-left py-3 px-4">Student</th>
                      <th className="text-left py-3 px-4">Period</th>
                      <th className="text-right py-3 px-4">kWh</th>
                      <th className="text-right py-3 px-4">Limit</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-right py-3 px-4">Amount Due</th>
                      <th className="text-left py-3 px-4">Payment</th>
                      <th className="text-center py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((record) => (
                      <tr key={record.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 font-medium">{record.roomTypeName}</td>
                        <td className="py-3 px-4">{record.studentName || '—'}</td>
                        <td className="py-3 px-4">
                          {formatBillingPeriodDisplay(
                            record.billingPeriod.month,
                            record.billingPeriod.year
                          )}
                        </td>
                        <td className="py-3 px-4 text-right font-mono">
                          {record.consumptionKwh}
                        </td>
                        <td className="py-3 px-4 text-right font-mono">
                          {record.contractMonthlyLimit || '—'}
                        </td>
                        <td className="py-3 px-4">{getStatusBadge(record)}</td>
                        <td className="py-3 px-4 text-right font-mono">
                          {record.excessAmountEur != null
                            ? <span className="text-destructive font-semibold">€{record.excessAmountEur.toFixed(2)}</span>
                            : <span className="text-muted-foreground">—</span>
                          }
                        </td>
                        <td className="py-3 px-4">{getPaymentBadge(record)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendEmail(record)}
                              disabled={!record.studentEmail || sendNotification.isPending}
                              title="Send email notification"
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(record)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                {record.exceedsLimit && record.paymentStatus === 'unpaid' && (
                                  <DropdownMenuItem
                                    onClick={() => markPaidMutation.mutate(record)}
                                    disabled={markPaidMutation.isPending}
                                    className="text-emerald-700"
                                  >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Mark as Paid
                                  </DropdownMenuItem>
                                )}
                                {record.exceedsLimit && record.paymentStatus === 'paid' && (
                                  <DropdownMenuItem
                                    onClick={() => markUnpaidMutation.mutate(record)}
                                    disabled={markUnpaidMutation.isPending}
                                    className="text-destructive"
                                  >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    Mark as Unpaid
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => handleDelete(record)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="mt-6 pt-4 border-t flex gap-6 text-sm">
                <div>
                  <span className="text-muted-foreground">Total records: </span>
                  <span className="font-semibold">{summary.totalRecords}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Exceeded limit: </span>
                  <span className="font-semibold text-destructive">
                    {summary.exceededCount}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Pending notifications: </span>
                  <span className="font-semibold text-warning">
                    {summary.pendingNotifications}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Unpaid: </span>
                  <span className="font-semibold text-destructive">
                    {filteredRecords.filter(r => r.paymentStatus === 'unpaid').length}
                  </span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Consumption Record</DialogTitle>
          </DialogHeader>
          {recordToEdit && (
            <div className="space-y-4 py-4">
              {/* Record Info */}
              <div className="p-3 bg-muted rounded-md text-sm space-y-1">
                <div><strong>Room:</strong> {recordToEdit.roomTypeName}</div>
                <div><strong>Student:</strong> {recordToEdit.studentName || '—'}</div>
                <div><strong>Period:</strong> {formatBillingPeriodDisplay(recordToEdit.billingPeriod.month, recordToEdit.billingPeriod.year)}</div>
                {recordToEdit.contractMonthlyLimit && (
                  <div><strong>Limit:</strong> {recordToEdit.contractMonthlyLimit} kWh</div>
                )}
              </div>

              {/* Editable Field */}
              <div className="space-y-2">
                <Label htmlFor="consumptionKwh">Consumption (kWh) *</Label>
                <Input
                  id="consumptionKwh"
                  type="number"
                  step="0.01"
                  placeholder="0"
                  value={editFormData.consumptionKwh || ""}
                  onChange={(e) => setEditFormData({ consumptionKwh: parseFloat(e.target.value) || 0 })}
                />
              </div>

              {/* Show if exceeds limit */}
              {recordToEdit.contractMonthlyLimit && editFormData.consumptionKwh > recordToEdit.contractMonthlyLimit && (
                <div className="p-3 bg-destructive/10 rounded-md text-sm text-destructive">
                  <strong>Warning:</strong> This consumption exceeds the limit by {(editFormData.consumptionKwh - recordToEdit.contractMonthlyLimit).toFixed(1)} kWh
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Consumption Record</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>Are you sure you want to delete this consumption record?</p>
                {recordToDelete && (
                  <div className="mt-3 p-3 bg-muted rounded-md text-sm">
                    <div><strong>Room:</strong> {recordToDelete.roomTypeName}</div>
                    <div><strong>Student:</strong> {recordToDelete.studentName || '—'}</div>
                    <div><strong>Period:</strong> {formatBillingPeriodDisplay(recordToDelete.billingPeriod.month, recordToDelete.billingPeriod.year)}</div>
                    <div><strong>Consumption:</strong> {recordToDelete.consumptionKwh} kWh</div>
                    {recordToDelete.linkedRecordIds?.length > 0 && (
                      <div className="mt-2 text-amber-700">
                        ⚠️ This room has {recordToDelete.linkedRecordIds.length} other occupant(s). All linked records will also be deleted.
                      </div>
                    )}
                  </div>
                )}
                <p className="mt-2">This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
